import {
  ButtonDS,
  DialogModalDS,
  Spacer,
  TextCapitalized,
  triggerToast,
} from "@qivia/ui";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { selectAdminUser } from "../homeSlice";
import { UpdateSivDataCarIconStatusPayload } from "./carIconsApi";
import {
  carIconPresignedUrlAsync,
  carIconsSlice,
  selectCarIconPresignedUrl,
  selectCarIconPresignedUrlStatus,
  selectCarIconsToReview,
  selectUpdateCarIconStatusStatus,
  updateCarIconStatusAsync,
} from "./carIconsSlice";
import "../../i18n/i18n";

export const CarIconModal = (props: {
  carIconUuid: string | null;
  onCloseModal: () => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { carIconUuid, onCloseModal } = props;
  const carIcon = useAppSelector(selectCarIconsToReview).find(
    (carIcon) => carIcon.uuid === carIconUuid,
  );
  const presignedUrl = useAppSelector(selectCarIconPresignedUrl);
  const presignedUrlStatus = useAppSelector(selectCarIconPresignedUrlStatus);
  const adminUser = useAppSelector(selectAdminUser);
  const updateCarIconStatusStatus = useAppSelector(
    selectUpdateCarIconStatusStatus,
  );
  const bucketFileName = carIcon?.bucketFileName;

  useEffect(() => {
    if (bucketFileName && presignedUrlStatus === "idle") {
      void dispatch(carIconPresignedUrlAsync({ bucketFileName }));
    }
  }, [bucketFileName, dispatch, presignedUrlStatus]);

  useEffect(() => {
    if (updateCarIconStatusStatus === "success") {
      triggerToast(t("carIcons.updateStatus.success") || "", "valid");
      dispatch(carIconsSlice.actions.resetUpdateCarIconStatusStatus());
    } else if (updateCarIconStatusStatus === "failed") {
      triggerToast(t("carIcons.updateStatus.failure") || "", "error");
      dispatch(carIconsSlice.actions.resetUpdateCarIconStatusStatus());
    }
  }, [dispatch, t, updateCarIconStatusStatus]);

  const closeModal = useCallback(() => {
    dispatch(carIconsSlice.actions.resetCarIconPresignedUrl());
    onCloseModal();
  }, [dispatch, onCloseModal]);

  const updateStatus = useCallback(
    (status: UpdateSivDataCarIconStatusPayload["status"]) => {
      if (!carIconUuid || !adminUser) return;
      void dispatch(
        updateCarIconStatusAsync({
          uuid: carIconUuid,
          status,
          updatedBy: adminUser.email,
        }),
      );
      closeModal();
    },
    [adminUser, carIconUuid, closeModal, dispatch],
  );

  if (!carIcon) {
    return;
  }

  return (
    <DialogModalDS
      visible
      onClose={closeModal}
      title={`${carIcon.brand} ${carIcon.model}`}
      width={40}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            text={t("carIcons.modal.reject")}
            format="fill"
            buttonType="secondary"
            onClick={() => updateStatus("REJECTED")}
          />
        ),
        rightButton: (
          <ButtonDS
            text={t("carIcons.modal.validate")}
            format="fill"
            buttonType="primary"
            onClick={() => updateStatus("VALIDATED")}
          />
        ),
      }}
    >
      <StyledColumn>
        <TextCapitalized>
          {`${t("carIcons.column.year")} : ${carIcon.year}`}
        </TextCapitalized>
        <TextCapitalized>
          {`${t("carIcons.column.color")} : ${carIcon.color}`}
        </TextCapitalized>
        <Spacer y={1} />
        {presignedUrl && <StyledImage src={presignedUrl} />}
      </StyledColumn>
    </DialogModalDS>
  );
};

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledImage = styled.img`
  width: 100%;
`;
